<template>
  <div class="field-palette">
    <!-- Tabs -->
    <div class="palette-tabs">
      <button
        class="tab-btn"
        :class="{ 'active': activeTab === 'fields' }"
        @click="activeTab = 'fields'"
      >
        <i class="bx bx-library"></i>
        Campos
      </button>
      <button
        class="tab-btn"
        :class="{ 'active': activeTab === 'custom' }"
        @click="activeTab = 'custom'"
      >
        <i class="bx bx-plus-circle"></i>
        Customizado
      </button>
    </div>

    <!-- Search -->
    <div class="palette-search">
      <i class="bx bx-search"></i>
      <input
        v-model="search"
        type="text"
        :placeholder="activeTab === 'fields' ? 'Buscar campos...' : 'Buscar tipos...'"
      />
      <button v-if="search" class="clear-search" @click="search = ''">
        <i class="bx bx-x"></i>
      </button>
    </div>

    <!-- Fields Tab Content -->
    <div v-if="activeTab === 'fields'" class="palette-content">
      <div v-if="loading" class="loading-state">
        <b-spinner small></b-spinner>
        <span>Carregando...</span>
      </div>

      <template v-else>
        <!-- Registration Control Fields Category -->
        <div v-if="isRegistrationControlEnabled" class="field-category rc-category">
          <div
            class="category-header rc-header"
            :class="{ 'expanded': expandedCategories.registration_control }"
            @click="toggleCategory('registration_control')"
          >
            <div class="category-info">
              <i class="bx bx-id-card" style="color: #e67e22;"></i>
              <span>Controle de Inscrição</span>
            </div>
            <div class="category-meta">
              <span class="field-count rc-count">{{ filteredRcFields.length }}</span>
              <i class="bx bx-chevron-down expand-icon"></i>
            </div>
          </div>

          <div v-show="expandedCategories.registration_control" class="category-fields">
            <div v-if="rcLoading" class="loading-state" style="padding: 16px;">
              <b-spinner small></b-spinner>
              <span>Carregando...</span>
            </div>
            <template v-else>
              <div
                v-for="field in filteredRcFields"
                :key="field.id"
                class="field-item rc-item"
                :class="{ 'field-disabled': field.already_added }"
                @click="addRcField(field)"
              >
                <div class="field-icon" style="background: #fef3e2;">
                  <i class="bx bx-id-card" style="color: #e67e22;"></i>
                </div>
                <div class="field-info">
                  <span class="field-name">{{ field.label }}</span>
                  <span v-if="field.already_added" class="field-type-desc">Já adicionado</span>
                </div>
                <button v-if="!field.already_added" class="btn-add" style="background: #e67e22;" title="Adicionar campo">
                  <i class="bx bx-plus"></i>
                </button>
                <i v-else class="bx bx-check" style="color: #e67e22; font-size: 18px;"></i>
              </div>
            </template>
          </div>
        </div>

        <!-- Standard Fields Category -->
        <div class="field-category">
          <div
            class="category-header"
            :class="{ 'expanded': expandedCategories.standard }"
            @click="toggleCategory('standard')"
          >
            <div class="category-info">
              <i class="bx bx-bookmark"></i>
              <span>Campos Padrão</span>
            </div>
            <div class="category-meta">
              <span class="field-count">{{ filteredStandardFields.length }}</span>
              <i class="bx bx-chevron-down expand-icon"></i>
            </div>
          </div>

          <div v-show="expandedCategories.standard" class="category-fields">
            <div
              v-for="field in filteredStandardFields"
              :key="field.name"
              class="field-item"
              draggable="true"
              @dragstart="onDragStart($event, field, 'standard')"
              @click="addStandardField(field)"
            >
              <div class="field-info">
                <span class="field-name">{{ field.label }}</span>
              </div>
              <button class="btn-add" title="Adicionar campo">
                <i class="bx bx-plus"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Form Fields Category -->
        <div class="field-category">
          <div
            class="category-header"
            :class="{ 'expanded': expandedCategories.form_fields }"
            @click="toggleCategory('form_fields')"
          >
            <div class="category-info">
              <i class="bx bx-collection"></i>
              <span>Campos de Formulários</span>
            </div>
            <div class="category-meta">
              <span class="field-count">{{ filteredFormFields.length }}</span>
              <i class="bx bx-chevron-down expand-icon"></i>
            </div>
          </div>

          <div v-show="expandedCategories.form_fields" class="category-fields">
            <div
              v-for="field in filteredFormFields"
              :key="'form_' + field.id"
              class="field-item"
              draggable="true"
              @dragstart="onDragStart($event, field, 'form_field')"
              @click="addFormField(field)"
            >
              <div class="field-info">
                <span class="field-name">{{ field.label }}</span>
              </div>
              <button class="btn-add" title="Adicionar campo">
                <i class="bx bx-plus"></i>
              </button>
            </div>
          </div>
        </div>

        <div v-if="hasNoResults" class="empty-state">
          <i class="bx bx-search-alt"></i>
          <p>Nenhum campo encontrado</p>
        </div>
      </template>
    </div>

    <!-- Custom Tab Content -->
    <div v-else class="palette-content">
      <!-- Step 1: Select Field Format -->
      <div v-if="!customFieldForm.selectedType" class="custom-select-type">
        <div class="custom-intro">
          <p>Crie campos internos para uso da equipe:</p>
        </div>

        <div class="field-types-list">
          <div
            v-for="fieldType in filteredFieldTypes"
            :key="fieldType.name"
            class="field-item field-type-item"
            @click="selectFieldType(fieldType)"
          >
            <div class="field-icon">
              <i :class="fieldType.icon"></i>
            </div>
            <div class="field-info">
              <span class="field-name">{{ fieldType.label }}</span>
              <span class="field-type-desc">{{ fieldType.description }}</span>
            </div>
            <i class="bx bx-chevron-right"></i>
          </div>
        </div>

        <div v-if="filteredFieldTypes.length === 0" class="empty-state">
          <i class="bx bx-search-alt"></i>
          <p>Nenhum formato encontrado</p>
        </div>
      </div>

      <!-- Step 2: Configure Field -->
      <div v-else class="custom-configure-field">
        <div class="config-header">
          <button class="btn-back-config" @click="cancelCustomField">
            <i class="bx bx-arrow-back"></i>
          </button>
          <div class="config-title">
            <i :class="customFieldForm.selectedType.icon"></i>
            <span>{{ customFieldForm.selectedType.label }}</span>
          </div>
        </div>

        <div class="config-form">
          <div class="form-group">
            <label>Label do Campo *</label>
            <input
              v-model="customFieldForm.label"
              type="text"
              class="form-control form-control-sm"
              placeholder="Ex: Observações Internas"
            />
          </div>

          <div class="form-group">
            <label>Nome do Campo (ID)</label>
            <input
              v-model="customFieldForm.name"
              type="text"
              class="form-control form-control-sm"
              placeholder="Ex: observacoes_internas"
            />
            <small class="text-muted">Use snake_case, sem espaços</small>
          </div>

          <div class="form-group">
            <label>Placeholder</label>
            <input
              v-model="customFieldForm.placeholder"
              type="text"
              class="form-control form-control-sm"
              placeholder="Texto de exemplo..."
            />
          </div>

          <div class="form-group">
            <label>Texto de Ajuda</label>
            <input
              v-model="customFieldForm.help_text"
              type="text"
              class="form-control form-control-sm"
              placeholder="Instruções para o usuário..."
            />
          </div>

          <!-- Options for select, radio, checkbox -->
          <div v-if="customFieldTypeHasOptions" class="form-group">
            <label>Opções</label>
            <div class="options-list">
              <div
                v-for="(opt, index) in customFieldForm.options"
                :key="index"
                class="option-item"
              >
                <input
                  v-model="opt.label"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Label"
                  @input="updateOptionValue(index)"
                />
                <button
                  v-if="customFieldForm.options.length > 1"
                  class="btn-remove-option"
                  @click="removeOption(index)"
                >
                  <i class="bx bx-x"></i>
                </button>
              </div>
              <button class="btn-add-option" @click="addOption">
                <i class="bx bx-plus"></i> Adicionar opção
              </button>
            </div>
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="customFieldForm.required" />
              <span>Campo obrigatório</span>
            </label>
          </div>

          <div class="config-actions">
            <button class="btn-cancel" @click="cancelCustomField">
              Cancelar
            </button>
            <button
              class="btn-create"
              @click="createCustomField"
              :disabled="!customFieldForm.label"
            >
              <i class="bx bx-plus"></i>
              Adicionar Campo
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import services from '../services'

export default {
  name: 'FieldPalette',
  props: {
    availableFields: {
      type: Object,
      default: function () { return { standard: [], form_fields: [] } }
    },
    loading: {
      type: Boolean,
      default: false
    },
    tabs: {
      type: Array,
      default: function () { return [] }
    },
    settings: {
      type: Object,
      default: function () { return {} }
    }
  },
  data: function () {
    return {
      activeTab: 'fields',
      search: '',
      expandedCategories: {
        standard: true,
        form_fields: true,
        registration_control: true
      },
      rcFields: [],
      rcLoading: false,
      customFieldForm: {
        selectedType: null,
        label: '',
        name: '',
        placeholder: '',
        help_text: '',
        required: false,
        options: [{ label: '', value: '' }]
      },
      fieldTypes: [
        { name: 'text', label: 'Texto', icon: 'bx bx-text', description: 'Campo de texto simples' },
        { name: 'textarea', label: 'Área de Texto', icon: 'bx bx-align-left', description: 'Texto multilinha' },
        { name: 'number', label: 'Número', icon: 'bx bx-hash', description: 'Apenas números' },
        { name: 'select', label: 'Lista Suspensa', icon: 'bx bx-chevron-down', description: 'Seleção única' },
        { name: 'multiselect', label: 'Seleção Múltipla', icon: 'bx bx-list-check', description: 'Várias opções' },
        { name: 'checkbox', label: 'Checkbox', icon: 'bx bx-checkbox-checked', description: 'Caixa de marcação' },
        { name: 'radio', label: 'Radio', icon: 'bx bx-radio-circle-marked', description: 'Escolha única' },
        { name: 'date', label: 'Data', icon: 'bx bx-calendar', description: 'Seletor de data' },
        { name: 'datetime', label: 'Data e Hora', icon: 'bx bx-calendar-event', description: 'Data com hora' },
        { name: 'toggle', label: 'Toggle', icon: 'bx bx-toggle-right', description: 'Liga/Desliga' }
      ]
    }
  },
  computed: {
    // Lista de campos já adicionados nas tabs
    addedFieldsMap: function () {
      var map = {}

      if (!this.tabs || this.tabs.length === 0) {
        console.log('[FieldPalette] addedFieldsMap: No tabs')
        return map
      }

      console.log('[FieldPalette] addedFieldsMap: Processing tabs', this.tabs.length)

      this.tabs.forEach(function (tab) {
        if (tab.fields && tab.fields.length > 0) {
          console.log('[FieldPalette] Tab', tab.name, 'has', tab.fields.length, 'fields')
          tab.fields.forEach(function (field) {
            console.log('[FieldPalette] Processing field:', {
              id: field.id,
              field_source: field.field_source,
              standard_field_name: field.standard_field_name,
              form_field_id: field.form_field_id,
              form_field: field.form_field
            })

            // Standard fields
            if (field.field_source === 'standard' && field.standard_field_name) {
              var standardKey = 'standard_' + field.standard_field_name
              map[standardKey] = true
              console.log('[FieldPalette] Added to map:', standardKey)
            }
            // Form fields - marcar tanto por ID quanto por NOME
            if (field.field_source === 'form_field') {
              // Marcar por ID
              if (field.form_field_id) {
                var formKey = 'form_' + field.form_field_id
                map[formKey] = true
                console.log('[FieldPalette] Added to map:', formKey)
              }
              // TAMBÉM marcar por NOME para evitar duplicação
              if (field.form_field && field.form_field.name) {
                var formNameKey = 'form_name_' + field.form_field.name
                map[formNameKey] = true
                console.log('[FieldPalette] Added to map by name:', formNameKey)
              }
            }
            // Internal fields (pelo nome)
            if (field.field_source === 'internal') {
              var internalName = (field.field_config && field.field_config.name) || field.name || field.id
              var internalKey = 'internal_' + internalName
              map[internalKey] = true
              console.log('[FieldPalette] Added to map:', internalKey)
            }
          })
        }
      })

      console.log('[FieldPalette] Final addedFieldsMap:', map)
      return map
    },

    standardFields: function () {
      if (!this.availableFields || !this.availableFields.standard) return []
      var self = this

      // Filtrar campos que já foram adicionados
      return this.availableFields.standard.filter(function (field) {
        var key = 'standard_' + field.name
        return !self.addedFieldsMap[key]
      })
    },

    formFields: function () {
      if (!this.availableFields || !this.availableFields.form_fields) return []

      var self = this
      var allFields = []
      var seenNames = {}

      console.log('[FieldPalette] formFields: Processing form_fields', this.availableFields.form_fields.length)
      console.log('[FieldPalette] formFields: addedFieldsMap', self.addedFieldsMap)

      this.availableFields.form_fields.forEach(function (formGroup) {
        if (formGroup.fields && formGroup.fields.length > 0) {
          formGroup.fields.forEach(function (field) {
            // Use field name as unique key to avoid duplicates
            var key = field.name || ('field_' + field.id)

            // Checar se já foi adicionado (por ID ou por NOME)
            var addedKeyById = 'form_' + field.id
            var addedKeyByName = 'form_name_' + field.name

            var isInSeenNames = !!seenNames[key]
            var isInAddedMapById = !!self.addedFieldsMap[addedKeyById]
            var isInAddedMapByName = !!self.addedFieldsMap[addedKeyByName]
            var isInAddedMap = isInAddedMapById || isInAddedMapByName

            console.log('[FieldPalette] Checking field:', {
              id: field.id,
              label: field.label,
              name: field.name,
              key: key,
              addedKeyById: addedKeyById,
              addedKeyByName: addedKeyByName,
              isInSeenNames: isInSeenNames,
              isInAddedMapById: isInAddedMapById,
              isInAddedMapByName: isInAddedMapByName,
              isInAddedMap: isInAddedMap
            })

            if (!isInSeenNames && !isInAddedMap) {
              seenNames[key] = true
              allFields.push({
                id: field.id,
                name: field.name,
                label: field.label,
                type: field.type,
                icon: field.icon || 'bx bx-text',
                form_name: formGroup.form.name,
                form_id: formGroup.form.id,
                field_type: field.field_type
              })
              console.log('[FieldPalette] Field ADDED to list:', field.label)
            } else {
              console.log('[FieldPalette] Field FILTERED OUT:', field.label, '(by name check)')
            }
          })
        }
      })

      console.log('[FieldPalette] formFields: Final list', allFields.length, 'fields')
      return allFields
    },

    filteredStandardFields: function () {
      if (!this.search) return this.standardFields
      var searchLower = this.search.toLowerCase()
      return this.standardFields.filter(function (field) {
        return (field.label && field.label.toLowerCase().indexOf(searchLower) !== -1) ||
               (field.name && field.name.toLowerCase().indexOf(searchLower) !== -1)
      })
    },

    filteredFormFields: function () {
      if (!this.search) return this.formFields
      var searchLower = this.search.toLowerCase()
      return this.formFields.filter(function (field) {
        return (field.label && field.label.toLowerCase().indexOf(searchLower) !== -1) ||
               (field.name && field.name.toLowerCase().indexOf(searchLower) !== -1) ||
               (field.form_name && field.form_name.toLowerCase().indexOf(searchLower) !== -1)
      })
    },

    filteredFieldTypes: function () {
      if (!this.search) return this.fieldTypes
      var searchLower = this.search.toLowerCase()
      return this.fieldTypes.filter(function (type) {
        return type.label.toLowerCase().indexOf(searchLower) !== -1 ||
               type.name.toLowerCase().indexOf(searchLower) !== -1
      })
    },

    hasNoResults: function () {
      return this.filteredStandardFields.length === 0 &&
             this.filteredFormFields.length === 0
    },

    customFieldTypeHasOptions: function () {
      if (!this.customFieldForm.selectedType) return false
      var optionTypes = ['select', 'multiselect', 'radio', 'checkbox']
      return optionTypes.indexOf(this.customFieldForm.selectedType.name) !== -1
    },

    isRegistrationControlEnabled: function () {
      console.log('[RC-CardBuilder] isRegistrationControlEnabled check:', {
        settings: this.settings,
        enabled: this.settings && this.settings.registration_control_enabled,
        id: this.settings && this.settings.registration_control_id
      })
      return this.settings &&
        this.settings.registration_control_enabled &&
        this.settings.registration_control_id
    },

    registrationControlFields: function () {
      if (!this.isRegistrationControlEnabled || !this.rcFields.length) return []
      var self = this
      return this.rcFields.map(function (rcField) {
        return {
          id: 'rc_' + rcField.rc_field,
          label: rcField.label,
          name: 'rc_' + rcField.rc_field,
          rc_field: rcField.rc_field,
          rc_type: rcField.type || 'text',
          rc_mask: rcField.mask || null,
          rc_options: rcField.options || null,
          is_rc_field: true,
          icon: 'bx bx-id-card',
          already_added: self.isRcFieldAlreadyUsed(rcField.rc_field)
        }
      })
    },

    filteredRcFields: function () {
      if (!this.search) return this.registrationControlFields
      var searchLower = this.search.toLowerCase()
      return this.registrationControlFields.filter(function (field) {
        return (field.label && field.label.toLowerCase().indexOf(searchLower) !== -1) ||
               (field.name && field.name.toLowerCase().indexOf(searchLower) !== -1)
      })
    }
  },
  watch: {
    'settings.registration_control_id': function (newVal, oldVal) {
      console.log('[RC-CardBuilder] WATCH registration_control_id changed:', oldVal, '->', newVal)
      this.loadRegistrationControlFields()
    },
    'settings.registration_control_enabled': function (newVal, oldVal) {
      console.log('[RC-CardBuilder] WATCH registration_control_enabled changed:', oldVal, '->', newVal)
      this.loadRegistrationControlFields()
    }
  },
  mounted: function () {
    console.log('[RC-CardBuilder] FieldPalette MOUNTED, settings:', JSON.stringify(this.settings))
    this.loadRegistrationControlFields()
  },
  methods: {
    toggleCategory: function (key) {
      this.$set(this.expandedCategories, key, !this.expandedCategories[key])
    },

    onDragStart: function (event, field, source) {
      var data = { field: field, source: source }
      event.dataTransfer.setData('field', JSON.stringify(data))
      event.dataTransfer.effectAllowed = 'copy'
    },

    addStandardField: function (field) {
      this.$emit('add-field', {
        field_source: 'standard',
        standard_field_name: field.name,
        label: field.label,
        icon: field.icon
      })
    },

    addFormField: function (field) {
      this.$emit('add-field', {
        field_source: 'form_field',
        form_field_id: field.id,
        label: field.label,
        icon: field.icon
      })
    },

    selectFieldType: function (fieldType) {
      this.customFieldForm.selectedType = fieldType
      this.customFieldForm.label = ''
      this.customFieldForm.name = ''
      this.customFieldForm.placeholder = ''
      this.customFieldForm.help_text = ''
      this.customFieldForm.required = false
      this.customFieldForm.options = [{ label: '', value: '' }]
    },

    cancelCustomField: function () {
      this.customFieldForm.selectedType = null
      this.customFieldForm.label = ''
      this.customFieldForm.name = ''
      this.customFieldForm.placeholder = ''
      this.customFieldForm.help_text = ''
      this.customFieldForm.required = false
      this.customFieldForm.options = [{ label: '', value: '' }]
    },

    createCustomField: function () {
      if (!this.customFieldForm.label) return

      var self = this
      var fieldType = this.customFieldForm.selectedType

      // Build options array if applicable
      var options = null
      if (this.customFieldTypeHasOptions) {
        options = this.customFieldForm.options
          .filter(function (opt) { return opt.label.trim() })
          .map(function (opt) {
            return {
              label: opt.label,
              value: opt.value || self.slugify(opt.label)
            }
          })
        if (options.length === 0) {
          options = [{ label: 'Opção 1', value: 'opcao_1' }]
        }
      }

      var customField = {
        field_source: 'internal',
        field_type: fieldType.name,
        label: this.customFieldForm.label,
        name: this.customFieldForm.name || this.slugify(this.customFieldForm.label),
        placeholder: this.customFieldForm.placeholder,
        help_text: this.customFieldForm.help_text,
        required: this.customFieldForm.required,
        options: options,
        icon: fieldType.icon,
        is_internal: true
      }

      this.$emit('add-field', customField)
      this.cancelCustomField()
    },

    addOption: function () {
      this.customFieldForm.options.push({ label: '', value: '' })
    },

    removeOption: function (index) {
      this.customFieldForm.options.splice(index, 1)
    },

    updateOptionValue: function (index) {
      var opt = this.customFieldForm.options[index]
      if (opt.label && !opt.value) {
        opt.value = this.slugify(opt.label)
      }
    },

    slugify: function (text) {
      return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
    },

    loadRegistrationControlFields: function () {
      console.log('[RC-CardBuilder] loadRegistrationControlFields called, enabled:', this.isRegistrationControlEnabled)
      console.log('[RC-CardBuilder] settings:', JSON.stringify(this.settings))
      if (!this.isRegistrationControlEnabled) {
        this.rcFields = []
        return
      }
      var self = this
      this.rcLoading = true
      var controlId = this.settings.registration_control_id
      console.log('[RC-CardBuilder] Fetching RC fields for controlId:', controlId)
      services.getRegistrationControlFields(controlId)
        .then(function (response) {
          console.log('[RC-CardBuilder] API response.data:', JSON.stringify(response.data))
          // response.data = { data: [...fields] } (Axios wrapper + Laravel wrapper)
          var fields = (response.data && response.data.data) || response.data || []
          self.rcFields = Array.isArray(fields) ? fields : []
          console.log('[RC-CardBuilder] rcFields set to:', self.rcFields.length, 'items')
        })
        .catch(function (error) {
          console.error('[RC-CardBuilder] Error loading RC fields:', error)
          self.rcFields = []
        })
        .finally(function () {
          self.rcLoading = false
        })
    },

    isRcFieldAlreadyUsed: function (rcFieldName) {
      if (!this.tabs || this.tabs.length === 0) return false
      var found = false
      this.tabs.forEach(function (tab) {
        if (tab.fields && tab.fields.length > 0) {
          tab.fields.forEach(function (field) {
            if (field.field_source === 'registration_control' &&
                field.field_config &&
                field.field_config.rc_field === rcFieldName) {
              found = true
            }
          })
        }
      })
      return found
    },

    addRcField: function (rcField) {
      if (rcField.already_added) return
      this.$emit('add-field', {
        field_source: 'registration_control',
        label: rcField.label,
        icon: rcField.icon,
        is_rc_field: true,
        rc_field: rcField.rc_field,
        rc_type: rcField.rc_type,
        rc_mask: rcField.rc_mask,
        rc_options: rcField.rc_options
      })
    }
  }
}
</script>

<style scoped>
.field-palette {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
}

/* Tabs */
.palette-tabs {
  display: flex;
  border-bottom: 1px solid #e9ecef;
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 8px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.15s;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
}

.tab-btn:hover {
  color: #495057;
  background: #f8f9fa;
}

.tab-btn.active {
  color: #556ee6;
  border-bottom-color: #556ee6;
  background: transparent;
}

.tab-btn i {
  font-size: 16px;
}

/* Search */
.palette-search {
  padding: 12px 16px;
  position: relative;
  border-bottom: 1px solid #e9ecef;
}

.palette-search > i {
  position: absolute;
  left: 28px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 16px;
}

.palette-search input {
  width: 100%;
  padding: 10px 36px 10px 40px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 13px;
  background: #f8f9fa;
  transition: all 0.15s;
}

.palette-search input:focus {
  outline: none;
  border-color: #556ee6;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.1);
}

.clear-search {
  position: absolute;
  right: 28px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #adb5bd;
  cursor: pointer;
  padding: 4px;
}

.clear-search:hover {
  color: #495057;
}

/* Content */
.palette-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.palette-content::-webkit-scrollbar {
  width: 6px;
}

.palette-content::-webkit-scrollbar-track {
  background: transparent;
}

.palette-content::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}

.palette-content::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}

/* Category */
.field-category {
  margin-bottom: 4px;
}

.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.category-header:hover {
  background: #f0f2f5;
}

.category-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
}

.category-info i {
  font-size: 16px;
  color: #556ee6;
}

.category-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.field-count {
  font-size: 11px;
  font-weight: 500;
  color: #6c757d;
  background: #e9ecef;
  padding: 2px 8px;
  border-radius: 10px;
}

.expand-icon {
  font-size: 18px;
  color: #adb5bd;
  transition: transform 0.2s;
}

.category-header.expanded .expand-icon {
  transform: rotate(180deg);
}

/* Fields */
.category-fields {
  padding: 8px 0 8px 8px;
}

.field-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: all 0.15s;
}

.field-item:last-child {
  margin-bottom: 0;
}

.field-item:hover {
  border-color: #556ee6;
  background: #f8f9ff;
  box-shadow: 0 2px 4px rgba(85, 110, 230, 0.1);
}

.field-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4ff;
  border-radius: 8px;
  flex-shrink: 0;
}

.field-icon i {
  font-size: 16px;
  color: #556ee6;
}

.field-info {
  flex: 1;
  min-width: 0;
}

.field-name {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.field-type {
  display: block;
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.field-type-desc {
  display: block;
  font-size: 10px;
  color: #9ca3af;
  margin-top: 2px;
}

.btn-add {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #556ee6;
  color: #fff;
  border-radius: 6px;
  cursor: pointer;
  opacity: 0;
  transition: all 0.15s;
  flex-shrink: 0;
}

.field-item:hover .btn-add {
  opacity: 1;
}

.btn-add:hover {
  background: #485ec4;
}

/* States */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  color: #6c757d;
  font-size: 13px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  text-align: center;
}

.empty-state i {
  font-size: 32px;
  color: #d0d5dd;
  margin-bottom: 8px;
}

.empty-state p {
  margin: 0;
  font-size: 13px;
  color: #9ca3af;
}

/* Custom Field Configuration */
.custom-select-type {
  display: flex;
  flex-direction: column;
}

.custom-intro {
  padding: 8px 12px;
  margin-bottom: 8px;
}

.custom-intro p {
  margin: 0;
  font-size: 12px;
  color: #6c757d;
}

.field-types-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-types-list .field-item {
  margin-bottom: 0;
}

.field-type-item > i.bx-chevron-right {
  color: #adb5bd;
  font-size: 18px;
  flex-shrink: 0;
}

.field-type-item:hover > i.bx-chevron-right {
  color: #556ee6;
}

.custom-configure-field {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.config-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.btn-back-config {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  color: #495057;
  transition: all 0.15s;
}

.btn-back-config:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.config-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.config-title i {
  font-size: 18px;
  color: #556ee6;
}

.config-form {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.config-form::-webkit-scrollbar {
  width: 6px;
}

.config-form::-webkit-scrollbar-track {
  background: transparent;
}

.config-form::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}

.config-form::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}

.config-form .form-group {
  margin-bottom: 16px;
}

.config-form label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #495057;
  margin-bottom: 6px;
}

.config-form .form-control-sm {
  font-size: 13px;
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.checkbox-label span {
  font-size: 13px;
  color: #495057;
}

/* Options List */
.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.option-item .form-control-sm {
  flex: 1;
}

.btn-remove-option {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
}

.btn-remove-option:hover {
  background: #fecaca;
}

.btn-add-option {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px;
  border: 1px dashed #d0d5dd;
  background: transparent;
  border-radius: 6px;
  font-size: 12px;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-add-option:hover {
  border-color: #556ee6;
  color: #556ee6;
}

/* Config Actions */
.config-actions {
  display: flex;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
  margin-top: 16px;
}

.btn-cancel {
  flex: 1;
  padding: 10px 16px;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-cancel:hover {
  border-color: #d0d5dd;
  background: #f8f9fa;
}

.btn-create {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  background: #556ee6;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-create:hover:not(:disabled) {
  background: #485ec4;
}

.btn-create:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Registration Control */
.rc-category {
  margin-bottom: 8px;
}

.rc-header {
  background: #fef9f0 !important;
  border: 1px solid #fdebd0;
}

.rc-header:hover {
  background: #fef3e2 !important;
}

.rc-count {
  background: #fdebd0 !important;
  color: #e67e22 !important;
}

.rc-item:hover {
  border-color: #e67e22 !important;
  background: #fef9f0 !important;
  box-shadow: 0 2px 4px rgba(230, 126, 34, 0.1) !important;
}

.field-disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
}

.field-disabled:hover {
  border-color: #e9ecef !important;
  background: #fff !important;
  box-shadow: none !important;
}
</style>
